/**
 * The LexiStar dispatch board API (ST-00250): a plan day's order bubbles, the trucks allocated
 * to them (each a Planned trip of Plan & Dispatch) and their absorption. Shapes:
 * `types/lexistar-dispatch-board`. Server: server/modules/lexistar/dispatchboard, mounted at
 * /api/lexistar.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response BODY.
 *
 *   getBoard(query)                   → { date, shift, now, tenant_ids, orders, trucks }
 *   createOrder(input)                → order
 *   updateOrder(id, patch)            → order              (patch carries the version seen)
 *   allocate(orderId, input)          → { order, allocation, trip, warnings }
 *   release(allocationId, input)      → { order, allocation, trip }
 *   conflictOf(err)                   → the 409 body ({ code, current? }) or null
 *   errorText(err)                    → a sentence for a banner
 */
import api from './api';
import type {
  AllocateInput,
  AllocateResponse,
  BoardConflict,
  BoardOrder,
  DispatchBoardResponse,
  DispatchShift,
  OrderInput,
  OrderPatch,
  ReleaseInput,
  ReleaseResponse,
} from '../types/lexistar-dispatch-board';

const BASE = '/lexistar/dispatch-board';

/** Query parameters without the empty ones. */
function params(q: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

export interface BoardQuery {
  date?: string;
  shift?: DispatchShift | 'all';
  tenant_id?: number;
}

export async function getBoard(query: BoardQuery = {}): Promise<DispatchBoardResponse> {
  const res = await api.get(BASE, { params: params({ ...query }) });
  return res.data as DispatchBoardResponse;
}

export async function createOrder(input: OrderInput): Promise<BoardOrder> {
  const res = await api.post(`${BASE}/orders`, input);
  return (res.data as { order: BoardOrder }).order;
}

export async function updateOrder(id: string, patch: OrderPatch): Promise<BoardOrder> {
  const res = await api.patch(`${BASE}/orders/${encodeURIComponent(id)}`, patch);
  return (res.data as { order: BoardOrder }).order;
}

export async function allocate(orderId: string, input: AllocateInput): Promise<AllocateResponse> {
  const res = await api.post(`${BASE}/orders/${encodeURIComponent(orderId)}:allocate`, input);
  return res.data as AllocateResponse;
}

export async function release(allocationId: string, input: ReleaseInput): Promise<ReleaseResponse> {
  const res = await api.post(`${BASE}/allocations/${encodeURIComponent(allocationId)}:release`, input);
  return res.data as ReleaseResponse;
}

interface ApiError {
  response?: { status?: number; data?: Partial<BoardConflict> & { error?: string; message?: string; details?: string[] } };
  message?: string;
}

/** The board's 409 body (stale | duplicate | unavailable | state), else null. */
export function conflictOf(err: unknown): BoardConflict | null {
  const e = (err || {}) as ApiError;
  if (e.response?.status !== 409 || !e.response.data) return null;
  const d = e.response.data;
  return { error: d.error || 'Conflict', code: (d.code as BoardConflict['code']) || 'state', current: d.current };
}

/** One sentence for a banner: the server's error (and details), else the transport error. */
export function errorText(err: unknown, fallback = 'Something went wrong'): string {
  const e = (err || {}) as ApiError;
  const d = e.response?.data;
  if (d) {
    const parts = [d.error, ...(d.details || [])].filter(Boolean);
    if (parts.length) return parts.join(' — ');
    if (d.message) return d.message;
  }
  return e.message || fallback;
}
